import React, { useLayoutEffect, useRef, useState } from "react";
import { Link, matchPath, useLocation, useParams } from "react-router";
import { Menu, Tabs, useMediaQuery } from "@sscs/ui";
import { ArrowDropDownIcon, CheckIcon } from "@sscs/ui/icons";
import { variables } from "@sscs/ui/styles";
import {
  FILESTREAM_ROUTE,
  INSIGHTS_ROUTE,
  POLICY_CONFIG_ROUTE,
  PROJECTS_ROUTE,
  SOFTWARE_CATALOG_ROUTE,
} from "Constants";
import { useReports } from "Hooks";
import { areInsightsEnabled } from "Utils";
import { computeVisibleCount } from "../ReportTabs/computeVisibleCount";
import ReportTabs from "../ReportTabs/ReportTabs";
import { computeNavCollapsed } from "./computeNavCollapsed";
import styles from "./navigationTabs.module.scss";

type TabData = {
  value: string;
  link: string;
  label: string;
};

const createLinkRoute = (
  groupId: string | undefined,
  route: string,
  pathname: string,
): string => {
  const isMatch = Boolean(
    matchPath(
      {
        path: `/${route}/group/${groupId}`,
        end: false,
      },
      pathname,
    ),
  );

  // link to the current page if user is already on this route
  if (isMatch) {
    return pathname;
  } else {
    return groupId ? `/${route}/group/${groupId}` : route;
  }
};

const NavigationTabs = (): React.JSX.Element => {
  const { groupId } = useParams();
  const { pathname } = useLocation();
  const { reports } = useReports();
  const rootRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [fitsExpanded, setFitsExpanded] = useState(true);

  const hasReports = Object.keys(reports).length > 0;
  const wideEnoughForFullNav = useMediaQuery(
    `(min-width: ${variables.full_nav_min_width})`,
  );

  let activeTab: string | boolean = pathname.slice(1).split("/")[0];

  const tabsData: TabData[] = [
    {
      value: FILESTREAM_ROUTE,
      link: createLinkRoute(groupId, FILESTREAM_ROUTE, pathname),
      label: "File stream",
    },
    {
      value: PROJECTS_ROUTE,
      link: createLinkRoute(groupId, PROJECTS_ROUTE, pathname),
      label: "Projects",
    },
    {
      value: SOFTWARE_CATALOG_ROUTE,
      link: createLinkRoute(groupId, SOFTWARE_CATALOG_ROUTE, pathname),
      label: "Catalog",
    },
    ...(areInsightsEnabled()
      ? [
          {
            value: INSIGHTS_ROUTE,
            link: createLinkRoute(groupId, INSIGHTS_ROUTE, pathname),
            label: "Insights",
          },
        ]
      : []),
    {
      value: POLICY_CONFIG_ROUTE,
      link: createLinkRoute(groupId, POLICY_CONFIG_ROUTE, pathname),
      label: "Policies",
    },
  ];

  if (pathname === "/") {
    activeTab = FILESTREAM_ROUTE;
  } else if (!tabsData.some((tab) => pathname.startsWith(tab.link))) {
    activeTab = false;
  }

  const measureSignature = tabsData.map((tab) => tab.value).join("|");

  // Measure whether the expanded tabs fit their available space. The width read
  // is `.root`'s own width, which is set by the flex layout (not by whether the
  // tabs or the dropdown are rendered inside it), so the decision can't feed back
  // on itself. Only consulted in the no-reports branch of computeNavCollapsed.
  useLayoutEffect(() => {
    const root = rootRef.current;
    const measure = measureRef.current;
    if (!root || !measure) return;

    const recompute = () => {
      const tabs = Array.from(
        measure.querySelectorAll<HTMLElement>("[data-measure-tab]"),
      );
      const tabWidths = tabs.map((tab) => tab.getBoundingClientRect().width);
      const gap =
        tabs.length >= 2
          ? tabs[1].getBoundingClientRect().left -
            tabs[0].getBoundingClientRect().right
          : 0;
      setFitsExpanded(
        computeVisibleCount(tabWidths, 0, root.clientWidth, gap) ===
          tabWidths.length,
      );
    };

    let isMounted = true;
    const observer = new ResizeObserver(recompute);
    observer.observe(root);
    observer.observe(measure);
    recompute();
    // A first measurement taken against the fallback font mismeasures the tab
    // labels, and a webfont swap that leaves the box sizes unchanged does not
    // fire the observer.
    void document.fonts.ready.then(() => isMounted && recompute());

    return () => {
      isMounted = false;
      observer.disconnect();
    };
  }, [measureSignature]);

  const collapsed = computeNavCollapsed(
    hasReports,
    wideEnoughForFullNav,
    fitsExpanded,
  );
  const activeLabel =
    tabsData.find((tab) => tab.value === activeTab)?.label ?? "Pages";

  return (
    <div className={styles.root} ref={rootRef}>
      {collapsed ? (
        <nav aria-label="Spectra Assure main navigation">
          <button
            type="button"
            className={styles["nav-dropdown"]}
            aria-haspopup="menu"
            onClick={(event) => setAnchorEl(event.currentTarget)}
          >
            {activeLabel}
            <ArrowDropDownIcon />
          </button>
          <Menu.Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {tabsData.map((tab) => (
              <Menu.Item
                key={tab.value}
                component={Link}
                to={tab.link}
                selected={tab.value === activeTab}
                onClick={() => setAnchorEl(null)}
                className={styles["nav-menu-item"]}
              >
                {tab.label}
                {tab.value === activeTab && <CheckIcon />}
              </Menu.Item>
            ))}
          </Menu.Menu>
        </nav>
      ) : (
        <Tabs.Tabs
          value={activeTab}
          aria-label="Spectra Assure main navigation"
          classes={{
            root: styles["nav-tabs"],
            list: styles["flex-container"],
          }}
        >
          {tabsData.map((tab) => (
            <Tabs.Tab
              classes={{
                root: styles["tab-root"],
              }}
              key={tab.value}
              value={tab.value}
              label={tab.label}
              iconPosition="start"
              component={Link}
              to={tab.link}
            />
          ))}
        </Tabs.Tabs>
      )}

      <div className={styles["measure-strip"]} aria-hidden ref={measureRef}>
        <Tabs.Tabs value={false} classes={{ list: styles["flex-container"] }}>
          {tabsData.map((tab) => (
            <Tabs.Tab
              classes={{ root: styles["tab-root"] }}
              key={tab.value}
              value={tab.value}
              label={tab.label}
              iconPosition="start"
              data-measure-tab
            />
          ))}
        </Tabs.Tabs>
      </div>

      <ReportTabs />
    </div>
  );
};

export default NavigationTabs;
